/**
 * Shell dock — layout space that shell surfaces can reserve at the inline-end
 * edge of the app.
 *
 * A panel pinned over the page has to be dismissed before the page can be read
 * again, which is the wrong trade when the panel exists precisely to discuss
 * what the page is showing. A *docked* panel instead takes space away from the
 * main column, so both stay visible and usable at once.
 *
 * Making that work needs one piece of shared state: how much room the shell must
 * give up. This is that state, and nothing more. It is deliberately anonymous —
 * claims are keyed by an opaque id, and the shell never learns which feature is
 * asking (docs/adr/002-modular-feature-registry.md). Any number of surfaces may
 * claim space; the reservations add up.
 *
 * The panels themselves are still `position: fixed` and portalled to the body.
 * They have to be: the header carries `backdrop-blur`, and `backdrop-filter`
 * makes an element a containing block for fixed descendants, so a panel rendered
 * inside the header would be positioned against the header box. The dock only
 * supplies the offset that keeps the page out from underneath them.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";

interface DockApi {
  /** Total space reserved at the inline-end edge, in CSS pixels. */
  inset: number;
  /** Claim (or resize) `width` pixels under `id`. */
  reserve: (id: string, width: number) => void;
  /** Give the claim back. Idempotent. */
  release: (id: string) => void;
}

const NO_DOCK: DockApi = { inset: 0, reserve: () => {}, release: () => {} };

const DockContext = createContext<DockApi | null>(null);

export function DockProvider({ children }: { children: ReactNode }) {
  const [claims, setClaims] = useState<Record<string, number>>({});

  // Both writers bail out when nothing would change. A claim is set from an
  // effect in a component the shell re-renders, so returning a fresh object for
  // a no-op update would loop: render → effect → setState → render.
  const reserve = useCallback((id: string, width: number) => {
    setClaims((prev) => (prev[id] === width ? prev : { ...prev, [id]: width }));
  }, []);

  const release = useCallback((id: string) => {
    setClaims((prev) => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const inset = useMemo(
    () => Object.values(claims).reduce((total, width) => total + width, 0),
    [claims],
  );

  const value = useMemo<DockApi>(
    () => ({ inset, reserve, release }),
    [inset, reserve, release],
  );

  return <DockContext.Provider value={value}>{children}</DockContext.Provider>;
}

/** Falls back to an inert dock outside a provider, so surfaces stay renderable
 * in isolation (tests, storybook-style harnesses) without a shell around them. */
function useDock(): DockApi {
  return useContext(DockContext) ?? NO_DOCK;
}

/** Space the shell must leave free at the inline-end edge, in CSS pixels. */
// eslint-disable-next-line react-refresh/only-export-components -- hooks colocated with their provider
export function useDockInset(): number {
  return useDock().inset;
}

/**
 * Declaratively hold `width` pixels of dock space for as long as this component
 * is mounted. Pass `null` to hold nothing — a surface that is currently
 * overlaying rather than docking should not shrink the page behind it.
 */
// eslint-disable-next-line react-refresh/only-export-components -- hooks colocated with their provider
export function useDockClaim(id: string, width: number | null): void {
  const { reserve, release } = useDock();

  useEffect(() => {
    if (width == null) {
      release(id);
      return;
    }
    reserve(id, width);
  }, [id, width, reserve, release]);

  // Separate effect, stable deps: releasing must happen on unmount only, not
  // every time the width changes.
  useEffect(() => () => release(id), [id, release]);
}
